// Api helpers for the quest system. Two surfaces: admin (under
// /api/admin/quests) and player (under /api/me/quests), plus the
// public board (under /api/public/quests).

import { api } from '../../lib/api';
import type {
  PlayerQuest,
  PublicQuest,
  Quest,
  QuestAssignmentStatus,
  QuestAssignmentWithUser,
  QuestStatus,
} from './types';

export interface CreateQuestBody {
  team_id: string;
  title: string;
  description?: string;
  event_type?: string;
  // M5.15: multi-competency rewards. At least one split
  // is required (the api rejects empty splits arrays).
  // Each split carries xp + an optional coins field
  // (parallel XP + coin ledger per ADR-0015 §3).
  splits: Array<{ competency_id: string; xp: number; coins?: number }>;
  mode: 'assigned' | 'open';
  max_completions?: number;
  review_mode?: 'auto' | 'review';
  // M5.10: same user can claim+complete the quest
  // multiple times. Each completion counts against
  // max_completions (option A from the design
  // discussion).
  repeatable?: boolean;
  due_at?: string | null;
  assign_to?: string[];
}

export interface UpdateQuestBody {
  title?: string;
  description?: string;
  event_type?: string;
  // M5.15: PATCH replaces the splits array wholesale;
  // there's no partial-merge of individual entries.
  // The api validates the same way as Create (including
  // the new optional `coins` per split).
  splits?: Array<{ competency_id: string; xp: number; coins?: number }>;
  review_mode?: 'auto' | 'review';
  due_at?: string | null;
  status?: 'open' | 'paused' | 'completed' | 'cancelled' | 'archived';
  // v0.8.9: post-creation edits for the rest of the quest
  // definition, so an operator can change their mind after
  // assigning.
  mode?: 'assigned' | 'open';
  max_completions?: number;
  repeatable?: boolean;
  // Syncs the assignment rows for an 'assigned' quest:
  // adds new users, cancels only untouched 'available'
  // rows for users dropped from the list.
  assign_to?: string[];
}

export const questsApi = {
  // Admin
  list: (params?: { team_id?: string; status?: QuestStatus }) => {
    // Filter out undefined AND empty-string values. URLSearchParams
    // converts undefined to the literal string "undefined" rather
    // than dropping the key, so we have to do it ourselves.
    const defined: Record<string, string> = {};
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') defined[k] = String(v);
      }
    }
    const qs = Object.keys(defined).length > 0
      ? '?' + new URLSearchParams(defined).toString()
      : '';
    return api<{ quests: (Quest & { claim_count: number })[] }>(`/api/admin/quests${qs}`);
  },
  get: (id: string) =>
    api<{ quest: Quest; assignments: QuestAssignmentWithUser[] }>(`/api/admin/quests/${id}`),
  create: (body: CreateQuestBody) =>
    api<{ quest: Quest }>('/api/admin/quests', { method: 'POST', body }),
  update: (id: string, body: UpdateQuestBody) =>
    api<{ quest: Quest }>(`/api/admin/quests/${id}`, { method: 'PATCH', body }),
  cancel: (id: string) => api(`/api/admin/quests/${id}`, { method: 'DELETE' }),
  archive: (id: string) =>
    api<{ quest: { id: string; status: 'archived' } }>(`/api/admin/quests/${id}/archive`, { method: 'POST' }),
  restore: (id: string) =>
    api<{ quest: Quest }>(`/api/admin/quests/${id}/restore`, { method: 'POST' }),
  // M5.10: takes the attempt number to disambiguate
  // the right (quest, user, attempt) row to confirm.
  // For non-repeatable quests, attempt=1 is the only
  // valid value. Sent as a body field rather than a
  // path param so the route signature stays
  // backward-compatible (the pre-M5.10 route accepted
  // no body; the server now reads body.attempt with
  // attempt=1 as the default for any pre-existing
  // client that doesn't send the field).
  confirmCompletion: (questId: string, userId: string, attempt = 1) =>
    api<{ ok: true; attempt?: number; total_xp: number; total_coins: number; splits: Array<{ competency_id: string; xp: number; coins?: number }>; event_id: string }>(
      `/api/admin/quests/${questId}/confirm-completion/${userId}`,
      { method: 'POST', body: { attempt } },
    ),

  // Player
  myQuests: (teamId?: string) =>
    api<{ quests: PlayerQuest[] }>(
      `/api/me/quests${teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''}`,
    ),
  myHistory: () =>
    api<{
      history: (Quest & {
        status: QuestAssignmentStatus;
        claimed_at: string | null;
        completed_at: string | null;
      })[];
    }>('/api/me/quests/history'),
  claim: (id: string) => api(`/api/me/quests/${id}/claim`, { method: 'POST' }),
  unclaim: (id: string) => api(`/api/me/quests/${id}/claim`, { method: 'DELETE' }),
  complete: (id: string) =>
    // M5.15: server now returns total_xp + total_coins
    // + splits (the sum of split xps + sum of split coins
    // + the per-competency breakdown). The old `xp` +
    // `competency_id` fields are gone.
    api<{ ok: true; status: 'completed' | 'pending_review'; total_xp?: number; total_coins?: number; splits?: Array<{ competency_id: string; xp: number; coins?: number }> }>(
      `/api/me/quests/${id}/complete`,
      { method: 'POST' },
    ),

  // Public board
  publicBoard: (teamId?: string) =>
    api<{ quests: PublicQuest[] }>(
      `/api/public/quests${teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''}`,
    ),
};
